import { TimezoneSelect } from "../../../components/ui/timezone-select";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Check, ChevronDown, ChevronUp, FileSpreadsheet, Play, Plus, Square, UploadCloud, X } from "lucide-react";
import { useRef, useState, useEffect, useMemo } from "react";
import { Dropdown } from "../../../components/ui/dropdown";
import { DateTimePicker } from "../../../components/ui/date-picker";
import { CountryCodePicker } from "../../../components/ui/country-code-picker";
import { useToast } from "../../../components/ui/toast";
import { ToggleRow } from "../../../components/ui/toggle-switch";
import { core } from "../../../lib/api";
import { autoMap, parseCsv } from "../../../lib/csv";
import { cn } from "../../../lib/utils";
import { MultiSelectAudioDropdown } from "../../../components/ui/multi-select-audio-dropdown";
import { useAgents, asArray, useWorkflows } from "../../home/queries";
import type { VoiceOption } from "../../home/types";
import { useAuth } from "../../../context/AuthContext";
import { getAgentScheduleSettings } from "../../../lib/userAccess";
import type { WizardState } from "./create-batch-wizard";
import type { RowError } from "./create-batch-wizard";
import { normalizeContactRows, type ContactField } from "./contact-utils";

type P = { s: WizardState; patch: (p: Partial<WizardState>) => void };

// Moved to components/ui/toggle-switch.tsx so every "Allow interruption" /
// "Background audio" style setting across the dashboard shares one switch,
// not a checkbox here and a checkbox there.
export const Toggle = ToggleRow;

/* ── STEP 1 · AI & Routing ── */
export function StepRouting({ s, patch }: P) {
  const { user } = useAuth();
  const toast = useToast();
  const agents = useAgents();
  const workflows = useWorkflows();
  const [playing, setPlaying] = useState(false);
  const audio = useRef<HTMLAudioElement | null>(null);
  const [showAdditional, setShowAdditional] = useState(false);

  const selectedWorkflow = workflows.data?.find((workflow) => (
    (workflow.config_id || workflow.id) === s.multiAgentConfigId
  ));
  const activeAgentId = s.multiAgentConfigId ? "" : s.agentId;

  const versions = useQuery({ queryKey: ["agents", activeAgentId, "versions", "summary"], queryFn: () => core<any>(`/api/v1/agents/${activeAgentId}/versions/summary`).then((d) => asArray<{ version_name: string, version_id: string }>(d)), enabled: !!activeAgentId, staleTime: 300_000 });
  const voices = useQuery({ queryKey: ["voice", "options"], queryFn: () => core<any>("/api/v1/voice/options").then((d) => asArray<VoiceOption>(d, "voices")), staleTime: Infinity });
  const dids = useQuery({ queryKey: ["voice", "dids"], queryFn: () => core<any>("/api/v1/voice/did-options").then((d) => asArray<{ did_id: string; number: string }>(d, "did_numbers")), staleTime: Infinity });
  const campaigns = useQuery({ queryKey: ["campaigns", "names"], queryFn: () => core<any>("/api/v1/campaigns/names").then((d) => asArray<{ name: string }>(d, "items")), staleTime: 300_000 });
  // 403 means the caller lacks the campaigns grant (default-off), not zero campaigns.
  const campaignsDenied = (campaigns.error as { status?: number } | null)?.status === 403;
  const backgrounds = useQuery({ queryKey: ["voice", "backgrounds"], queryFn: () => core<any>("/api/v1/voice/background-options").then((d) => asArray<{ id: string; name: string; kind: string }>(d, "background_voices")), staleTime: Infinity });

  const defaultedThinkingSoundsRef = useRef(false);
  useEffect(() => {
    if (defaultedThinkingSoundsRef.current) return;
    if (!backgrounds.data || !s.bgNoise || (s.backgroundAudio && s.backgroundAudio.length > 0)) return;

    const thinkingSounds = (backgrounds.data ?? [])
      .filter((b: any) => b.type === "thinking_sound")
      .map((b: any) => ({ ...b, id: b.id || b.file_key }));

    if (thinkingSounds.length === 0) return;

    let defaults = thinkingSounds.filter((b: any) => {
      const name = String(b.name || "").toLowerCase();
      return name.includes("mouse") || name.includes("click") || name.includes("keyboard") || name.includes("typing");
    });

    if (defaults.length === 0) {
      defaults = thinkingSounds.filter((b: any) => b.active !== false).slice(0, 2);
    }

    if (defaults.length > 0) {
      defaultedThinkingSoundsRef.current = true;
      patch({
        backgroundAudio: defaults.map((d: any) => ({ ...d, volume: d.volume ?? 0.3 })),
      });
    }
  }, [backgrounds.data, s.bgNoise, s.backgroundAudio, patch]);

  const agentOptions = [
    ...(agents.data?.map((agent) => ({ value: agent.agent_id, label: agent.agent_name || agent.name || agent.agent_id })) ?? []),
    ...(workflows.data?.map((workflow) => ({
      value: workflow.config_id || workflow.id || "",
      label: `${workflow.name || workflow.agent_name || workflow.config_id || workflow.id} (Workflow)`,
    })).filter((option) => !!option.value) ?? []),
  ];
  const versionOptions = selectedWorkflow
    ? [{
        value: String(selectedWorkflow.version_id || ""),
        label: String(selectedWorkflow.version_name || selectedWorkflow.version_id || "Workflow version"),
      }].filter((option) => !!option.value)
    : versions.data?.map((version: any) => {
        if (typeof version === "string") return { value: version, label: version };
        return {
          value: version.version_id || version.version_name || version.version || "",
          label: version.version_name || version.version || version.version_id || "",
        };
      }).filter((option) => !!option.value) ?? [];

  const scheduleSettings = (s.agentId || s.multiAgentConfigId)
    ? getAgentScheduleSettings(user, s.agentId, s.multiAgentConfigId)
    : null;

  const preview = async () => {
    if (playing) { audio.current?.pause(); setPlaying(false); return; }
    const selectedVoice = voices.data?.find((v: any) => (v.local_voice_name || v.id || v.voice_id) === s.voiceId);
    const previewUrl = selectedVoice?.preview_url;
    if (!previewUrl) {
      toast.danger("Voice preview unavailable", "No preview clip has been set for this voice yet.");
      return;
    }
    try {
      audio.current = new Audio(previewUrl);
      await audio.current.play();
      setPlaying(true);
      audio.current.onended = () => setPlaying(false);
    } catch (error) {
      setPlaying(false);
      toast.danger("Voice preview failed", (error as Error).message);
    }
  };

  return (
    <div className="space-y-4">
      {/* Row 1: Agent + Version */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <span className="mb-1 block font-mono text-[10px] font-semibold uppercase tracking-wider text-ink-3">Agent</span>
          <Dropdown disabled={s.isRetryLocked} ariaLabel="Agent" value={s.multiAgentConfigId || s.agentId} options={agentOptions} onChange={(v) => {
            const workflow = workflows.data?.find((item) => (item.config_id || item.id) === v);
            if (workflow) {
              patch({
                multiAgentConfigId: v,
                agentId: "",
                version: String(workflow.version_id || ""),
                normalized: [], rejectedContacts: [], duplicateContacts: [],
                rowErrors: [],
                mapping: {},
              });
            } else {
              patch({ agentId: v, multiAgentConfigId: "", version: "", normalized: [], rejectedContacts: [], duplicateContacts: [], rowErrors: [], mapping: {} });
            }
          }} />
        </div>
        <div>
          <span className="mb-1 block font-mono text-[10px] font-semibold uppercase tracking-wider text-ink-3">Version</span>
          <Dropdown disabled={s.isRetryLocked} ariaLabel="Version" value={s.version} options={versionOptions} onChange={(v) => patch({ version: v, normalized: [], rejectedContacts: [], duplicateContacts: [], rowErrors: [], mapping: {} })} />
        </div>
      </div>

      {/* Advanced Settings Transparency Info Box */}
      {scheduleSettings && (scheduleSettings.call_retry_enabled || scheduleSettings.short_call_retry_enabled || scheduleSettings.llm_skip_enabled || scheduleSettings.noise_cancellation || scheduleSettings.speaker_isolation || scheduleSettings.thinking_nextgen) && (
        <div className="rounded-xl border border-line bg-soft px-4 py-3 text-xs text-ink-3">
          <p className="mb-2 font-semibold text-ink-2">Agent Config Preview</p>
          <div className="flex flex-wrap gap-x-6 gap-y-2">
            {scheduleSettings.noise_cancellation && <div><span className="opacity-70">Noise cancellation:</span> <span className="font-mono font-medium text-ink-2">on</span></div>}
            {scheduleSettings.speaker_isolation && <div><span className="opacity-70">Speaker isolation:</span> <span className="font-mono font-medium text-ink-2">on</span></div>}
            {scheduleSettings.thinking_nextgen && <div><span className="opacity-70">Nextgen thinker:</span> <span className="font-mono font-medium text-ink-2">on</span></div>}
            {scheduleSettings.call_retry_enabled && (
              <>
                <div><span className="opacity-70">Retry attempts:</span> <span className="font-mono font-medium text-ink-2">{scheduleSettings.call_retry_attempts}</span></div>
                <div><span className="opacity-70">Delay (mins):</span> <span className="font-mono font-medium text-ink-2">{scheduleSettings.call_retry_delay_minutes}</span></div>
                <div><span className="opacity-70">Auto-disconnect:</span> <span className="font-mono font-medium text-ink-2">{scheduleSettings.auto_disconnect_timeout_seconds}s</span></div>
              </>
            )}
            {scheduleSettings.short_call_retry_enabled && <div><span className="opacity-70">Short call retry:</span> <span className="font-mono font-medium text-ink-2">{scheduleSettings.short_call_retry_seconds}s</span></div>}
            {scheduleSettings.llm_skip_enabled && <div><span className="opacity-70">LLM skip:</span> <span className="font-mono font-medium text-ink-2">{scheduleSettings.llm_skip_seconds}s</span></div>}
          </div>
        </div>
      )}

      {/* Row 2: Voice + Caller ID */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <span className="mb-1 block font-mono text-[10px] font-semibold uppercase tracking-wider text-ink-3">Voice</span>
          <div className="flex gap-2">
            <div className="flex-1">
              <Dropdown ariaLabel="Voice" value={s.voiceId} options={voices.data?.map((v: any) => ({ value: v.local_voice_name || v.id || v.voice_id || "", label: v.name || v.local_voice_name || v.voice_id || "" })).filter((option) => !!option.value) ?? []} onChange={(v) => patch({ voiceId: v })} />
            </div>
            <button onClick={preview} disabled={!s.voiceId} aria-label="Preview voice" className="h-10 rounded-lg bg-soft px-3 text-ink hover:bg-line disabled:opacity-40">
              {playing ? <Square className="h-4 w-4" /> : <Play className="h-4 w-4" />}
            </button>
          </div>
        </div>
        <div>
          <span className="mb-1 block font-mono text-[10px] font-semibold uppercase tracking-wider text-ink-3">Caller ID</span>
          <Dropdown ariaLabel="Caller ID" value={s.did} options={dids.data?.map((d: any) => {
            if (typeof d === 'string') return { value: d, label: d };
            return { value: d.did_id || d.id || d.number || "", label: d.number || d.name || d.did_id || "" };
          }) ?? []} onChange={(v) => patch({ did: v })} />
        </div>
      </div>

      {/* Campaign */}
      <div>
        <span className="mb-1 block font-mono text-[10px] font-semibold uppercase tracking-wider text-ink-3">Campaign</span>
        <div className="flex gap-2">
          <input value={s.campaign} onChange={(e) => patch({ campaign: e.target.value })} placeholder="Type campaign name..."
            onBlur={async () => { if (s.campaign.trim()) { await core(`/api/v1/campaigns/names?name=${encodeURIComponent(s.campaign.trim())}`, { method: "POST" }).catch(() => {}); } }}
            onKeyDown={async (e) => { if (e.key === "Enter" && s.campaign.trim()) { e.preventDefault(); await core(`/api/v1/campaigns/names?name=${encodeURIComponent(s.campaign.trim())}`, { method: "POST" }).catch(() => {}); } }}
            className="flex-1 rounded-lg border border-line px-3 py-2 text-sm focus:border-brand focus:outline-none" />
          
          <div className="w-48">
            <Dropdown ariaLabel="Previous campaigns" value={s.campaign} 
              options={[
                { value: "", label: "Select previous..." }, 
                ...(campaigns.data?.map((c: any) => {
                  if (typeof c === 'string') return { value: c, label: c };
                  return { value: c.name || c.id || "", label: c.name || c.id || "" };
                }) ?? [])
              ]} 
              onChange={(v) => patch({ campaign: v })} />
          </div>
          {campaignsDenied && (
            <p className="mt-1 text-[11px] text-ink-3">Previous campaigns unavailable — ask your organization owner for access.</p>
          )}
        </div>
      </div>

      {/* Additional Settings (collapsible) */}
      <div className="rounded-lg border border-line">
        <button onClick={() => setShowAdditional(x => !x)}
          className="flex w-full items-center justify-between px-4 py-3 text-sm font-semibold text-ink hover:bg-soft">
          Additional Settings
          {showAdditional ? <ChevronUp className="h-4 w-4 text-ink-3" /> : <ChevronDown className="h-4 w-4 text-ink-3" />}
        </button>
        {showAdditional && (
          <div className="border-t border-line p-4 space-y-3">
            <Toggle on={s.useAlternateNumber} onClick={() => patch({ useAlternateNumber: !s.useAlternateNumber })} label="Retry with Alternate Number" />
            <Toggle on={s.interruption} onClick={() => patch({ interruption: !s.interruption })} label="Allow user interruption" />
            <Toggle on={s.bgNoise} onClick={() => patch({ bgNoise: !s.bgNoise })} label="Background noise" sub="Unlocks ambience + thinking sounds" />
            <div className={cn("grid grid-cols-1 sm:grid-cols-2 gap-3", !s.bgNoise && "pointer-events-none opacity-40")}>
              <div>
                <span className="mb-1 block font-mono text-[10px] font-semibold uppercase tracking-wider text-ink-3">Ambience</span>
                <MultiSelectAudioDropdown
                  label="Ambience"
                  placeholder="Select ambience..."
                  options={(backgrounds.data ?? []).filter((b: any) => b.type === "ambient_sound").map((b: any) => ({ ...b, id: b.id || b.file_key }))}
                  value={s.ambience}
                  onChange={(v) => patch({ ambience: v })}
                />
              </div>
              <div>
                <span className="mb-1 block font-mono text-[10px] font-semibold uppercase tracking-wider text-ink-3">Thinking sound</span>
                <MultiSelectAudioDropdown
                  label="Thinking Sound"
                  placeholder="Select thinking sound..."
                  options={(backgrounds.data ?? []).filter((b: any) => b.type === "thinking_sound").map((b: any) => ({ ...b, id: b.id || b.file_key }))}
                  value={s.backgroundAudio}
                  onChange={(v) => patch({ backgroundAudio: v })}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ── STEP 3 · Contacts ── */
export function StepContacts({ s, patch, hideValidate }: P & { hideValidate?: boolean }) {
  const toast = useToast();
  const [parsing, setParsing] = useState(false);
  // Parsed-preview + validated-preview "view all" toggles. Collapsed shows
  // the first few rows; expanded renders every row inside a scrollable body.
  const [showAllPreview, setShowAllPreview] = useState(false);
  const [showAllValidated, setShowAllValidated] = useState(false);

  // A fresh upload gets the collapsed preview again.
  useEffect(() => {
    setShowAllPreview(false);
    setShowAllValidated(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [s.csvHeaders.join(",")]);
  
  const workflows = useWorkflows();
  const selectedWorkflow = s.multiAgentConfigId ? workflows.data?.find((w: any) => (w.config_id || w.id) === s.multiAgentConfigId) : null;
  
  const workflowInfieldsQuery = useQuery({
    queryKey: ["call-infields", selectedWorkflow?.call_infields_id],
    queryFn: () => core<any>(`/api/v1/call-infields/${selectedWorkflow?.call_infields_id}`),
    enabled: !!selectedWorkflow?.call_infields_id,
    staleTime: 300_000,
  });

  const agentVersionQuery = useQuery({
    queryKey: ["agents", s.agentId, "versions", s.version],
    queryFn: () => core<any>(`/api/v1/agents/${s.agentId}/versions/${s.version}`),
    enabled: !!s.agentId && !!s.version,
    staleTime: 300_000
  });

  const infields = useMemo(() => {
    const rawInfields = s.multiAgentConfigId
      ? (selectedWorkflow?.call_infields || workflowInfieldsQuery.data?.call_infields || workflowInfieldsQuery.data || [])
      : (agentVersionQuery.data?.call_infields ?? agentVersionQuery.data?.infields ?? []);
    return (Array.isArray(rawInfields) ? rawInfields : []).map((f: any) => ({
      key: f.key || f.fieldName || "",
      label: f.label || f.fieldName || f.key || "",
      required: !!f.required,
    })).filter((f: any) => !!f.key);
  }, [s.multiAgentConfigId, selectedWorkflow, workflowInfieldsQuery.data, agentVersionQuery.data]);
  const contactFields: ContactField[] = useMemo(() => [
    { key: "phone_number", label: "Phone number", required: true },
    ...infields.filter((field) => {
      const key = field.key.toLowerCase().replace(/[^a-z0-9]/g, "");
      return !["phone", "phonenumber", "mobile", "mobilenumber", "contactid", "id"].includes(key);
    }),
  ], [infields]);

  const hasPrefilledDummy = useRef(false);

  useEffect(() => {
    if (!hasPrefilledDummy.current && s.manualRows.length === 1 && (!s.manualRows[0].phone_number || s.manualRows[0].phone_number === "9999999999") && contactFields.length > 0) {
      hasPrefilledDummy.current = true;
      const row = { ...s.manualRows[0] };
      let changed = false;
      for (const field of contactFields) {
        if (!row[field.key]) {
          if (field.key === "name") row[field.key] = "dummy";
          else if (field.key === "phone_number") row[field.key] = "";
          else row[field.key] = "dummy";
          changed = true;
        }
      }
      if (changed) {
        patch({ manualRows: [row] });
      }
    }
  }, [contactFields, s.manualRows, patch]);

  // Required-field check runs locally (depends on this wizard's configured
  // contactFields/mapping, which the backend doesn't know about). Phone
  // format validity and duplicate detection are NOT checked locally at all —
  // every row that passes the required-field check is sent to
  // /schedules/validate-contacts, and the backend's normalize_phone_number_any_country
  // verdict is the only source of phone-invalid/duplicate rejections, so this
  // preview matches exactly what create_schedule will accept/reject at Launch
  // Batch. TTS infield normalisation still runs only in core create_schedule
  // at Launch Batch — not here.
  const validateContacts = useMutation({
    mutationFn: async () => {
      const rowsWithCc = s.contactSource === "csv"
        ? s.rawRows.map((row) => ({
            ...row,
            phone_number_cc: row.phone_number_cc || s.csvCountryCode || "+91",
          }))
        : s.rawRows;
      const selectedRows = s.contactSource === "csv"
        ? rowsWithCc
            .map((row, index) => ({ row, index }))
            .filter(({ index }) => !s.excludedRowIndices.includes(index))
        : s.manualRows
            .map((row, index) => ({ row, index }))
            .filter(({ row }) => Object.values(row).some(Boolean));
      const localResult = normalizeContactRows({
        rows: selectedRows.map(({ row }) => row),
        sourceIndices: selectedRows.map(({ index }) => index),
        fields: contactFields,
        mapping: s.contactSource === "csv" ? s.mapping : {},
      });

      if (localResult.normalized.length === 0) return { ...localResult, duplicateContacts: [] };

      const serverResult = await core<{
        contacts: { contact_id: string; phone_number: string }[];
        rejected_contacts_data: { contact_id: string; phone_number: string; rejection_reason: string }[];
        duplicate_contact_ids?: string[];
        existing_contact_ids?: string[];
      }>("/api/v1/schedules/validate-contacts", {
        method: "POST",
        body: JSON.stringify({
          contacts: localResult.normalized.map((c) => ({
            contact_id: c.contact_id,
            name: c.name,
            phone_number: c.phone_number,
          })),
        }),
      });

      const serverRejectedById = new Map(
        (serverResult.rejected_contacts_data || []).map((c) => [c.contact_id, c.rejection_reason]),
      );

      // Duplicate contact_id box: in-file dups (local, keeps source_row) +
      // already-exists (server, via schedule_contacts_client_contact_idx).
      // NOTE: no auto-id skip — every non-empty contact_id is checked, so
      // user-supplied ids like cont-AB12CD34 are still caught.
      const counts = new Map<string, number>();
      localResult.normalized.forEach((c) => {
        const id = String(c.contact_id || "").trim();
        if (!id) return;
        counts.set(id, (counts.get(id) || 0) + 1);
      });
      const dupInFile = new Set(
        [...counts.entries()].filter(([, n]) => n > 1).map(([id]) => id),
      );
      const existsSet = new Set([
        ...(serverResult.duplicate_contact_ids || []).map((id) => String(id)),
        ...(serverResult.existing_contact_ids || []),
      ]);
      const duplicateContacts = localResult.normalized
        .filter((c) => {
          const id = String(c.contact_id || "").trim();
          return id && (dupInFile.has(id) || existsSet.has(id));
        })
        .map((c) => {
          const id = String(c.contact_id || "").trim();
          const reasons: string[] = [];
          if (dupInFile.has(id)) reasons.push(`Duplicate in file (${counts.get(id)} rows)`);
          if (existsSet.has(id) && !(serverResult.duplicate_contact_ids || []).includes(id))
            reasons.push("Contact ID already exists");
          else if (existsSet.has(id) && dupInFile.has(id) && (serverResult.existing_contact_ids || []).includes(id))
            reasons.push("Contact ID already exists");
          else if ((serverResult.duplicate_contact_ids || []).includes(id) && !dupInFile.has(id))
            reasons.push("Duplicate in file");
          return { ...c, _dup_reason: reasons.join(" · ") || "Duplicate contact ID" };
        });

      if (serverRejectedById.size === 0)
        return { ...localResult, duplicateContacts, rejected: localResult.rejected, errors: localResult.errors };

      const normalized: typeof localResult.normalized = [];
      const rejected = [...localResult.rejected];
      const errors = [...localResult.errors];
      localResult.normalized.forEach((contact) => {
        const reason = serverRejectedById.get(contact.contact_id);
        if (reason) {
          rejected.push({ ...contact, rejection_reason: reason, status: "rejected" });
          errors.push({ row: Number(contact.source_row) - 1, field: "phone_number", reason });
        } else {
          normalized.push(contact);
        }
      });
      return { normalized, rejected, errors, deduped: localResult.deduped, duplicateContacts };
    },
    onSuccess: (result: any) => {
      patch({
        normalized: result.normalized,
        rejectedContacts: result.rejected,
        duplicateContacts: result.duplicateContacts || [],
        rowErrors: result.errors as RowError[],
        deduped: result.deduped,
        dndRemoved: 0,
      });
      if (result.normalized.length === 0) {
        toast.danger("No valid contacts", "Fix the row issues and validate again.");
      } else if (result.errors.length > 0) {
        toast.warning(`${result.errors.length} row issue(s)`, `${result.normalized.length} valid contacts are ready.`);
      } else {
        toast.success(`${result.normalized.length} contacts validated`);
      }
    },
    onError: (e) => toast.danger("Validation failed", (e as Error).message),
  });

  const onFile = async (f: File) => {
    setParsing(true);
    try {
      const { rows, headers } = await parseCsv(f);
      patch({ fileName: f.name, fileSize: f.size, rawRows: rows, csvHeaders: headers, mapping: autoMap(contactFields, headers), normalized: [], rejectedContacts: [], duplicateContacts: [], rowErrors: [], excludedRowIndices: [] });
    } catch { toast.danger("Couldn't parse CSV", "Header row + UTF-8 required."); }
    setParsing(false);
  };

  return (
    <div className="space-y-4">
      {!s.isRetryLocked && (
        <div className="inline-flex rounded-lg border border-line p-1">
          {(["csv", "manual"] as const).map((m) => (
            <button key={m} onClick={() => patch({ contactSource: m, normalized: [], rejectedContacts: [], duplicateContacts: [], rowErrors: [] })}
              className={cn("rounded-md px-4 py-1.5 text-sm font-semibold transition-colors", s.contactSource === m ? "bg-brand/15 text-brand font-bold" : "text-ink-3 hover:bg-soft")}>
              {m === "csv" ? "Upload CSV" : "Manual entry"}
            </button>
          ))}
        </div>
      )}

      {!s.isRetryLocked && (
        s.contactSource === "csv" ? (
        <>
          {/* Country code selector for CSV phone numbers */}
          <div className="flex items-center gap-3 rounded-xl border border-line bg-soft/30 px-4 py-3">
            <div className="shrink-0">
              <p className="text-xs font-semibold text-ink">Phone country code</p>
              <p className="mt-0.5 text-[10px] text-ink-3">Applied to all numbers in the CSV</p>
            </div>
            <div className="ml-auto">
              <CountryCodePicker
                align="right"
                value={s.csvCountryCode ?? "+91"}
                onChange={(cc) => patch({ csvCountryCode: cc, normalized: [], rejectedContacts: [], duplicateContacts: [], rowErrors: [] })}
              />
            </div>
          </div>

          {s.fileName ? (
            <div className="flex items-center justify-between rounded-xl border border-brand/20 bg-brand-soft/20 px-4 py-3">
              <div className="flex items-center gap-3">
                <FileSpreadsheet className="h-6 w-6 text-brand" />
                <div>
                  <p className="text-sm font-semibold text-brand">{s.fileName}</p>
                  <p className="text-xs text-brand/70">{s.fileSize ? `${(s.fileSize / 1024).toFixed(1)} KB` : ""}</p>
                </div>
              </div>
              <button onClick={() => patch({ fileName: null, fileSize: null, rawRows: [], csvHeaders: [], mapping: {}, normalized: [], rejectedContacts: [], duplicateContacts: [], rowErrors: [], excludedRowIndices: [] })}
                className="rounded-lg p-1.5 text-brand/60 hover:bg-brand/10 hover:text-brand" aria-label="Remove file">
                <X className="h-4 w-4" />
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              <label className="flex min-h-[220px] cursor-pointer flex-col items-center justify-center rounded-2xl border-2 border-dashed border-line p-10 text-center transition-colors hover:border-brand hover:bg-brand-soft/10">
                <UploadCloud className="h-12 w-12 text-ink-3" />
                <p className="mt-4 text-base font-semibold text-ink">{parsing ? "Parsing…" : "Drop your contact list here"}</p>
                <p className="mt-1 text-sm text-ink-3">or click to browse from your computer (CSV or Excel)</p>
                <input type="file" accept=".csv,text/csv,.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,.xls,application/vnd.ms-excel" className="hidden" onChange={(e) => e.target.files?.[0] && onFile(e.target.files[0])} />
              </label>
              {contactFields.length > 0 && (
                <div className="text-center">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      const headerRow = contactFields.map(f => `"${f.label || f.key}"`).join(",");
                      const dummyRow = contactFields.map(f => {
                        if (f.key === "phone_number") return "9999999999";
                        if (f.key === "name") return '"John Doe"';
                        return '"dummy"';
                      }).join(",");
                      const csvContent = `${headerRow}\n${dummyRow}`;
                      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement("a");
                      a.href = url;
                      a.download = "sample_contacts.csv";
                      document.body.appendChild(a);
                      a.click();
                      document.body.removeChild(a);
                      URL.revokeObjectURL(url);
                    }}
                    className="text-[11px] font-semibold text-brand hover:underline"
                  >
                    Download sample CSV format
                  </button>
                </div>
              )}
            </div>
          )}

          {s.csvHeaders.length > 0 && s.normalized.length === 0 && (
            <div className="rounded-xl border border-line bg-soft/30 p-4">
              <div className="mb-3">
                <p className="text-sm font-bold text-ink">Map contact fields</p>
                <p className="text-xs text-ink-3">Confirm which spreadsheet column supplies each field.</p>
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {contactFields.map((field) => (
                  <div key={field.key}>
                    <span className="mb-1 block truncate text-[10px] font-semibold uppercase text-ink-3">
                      {field.label} {field.required && <span className="text-danger">*</span>}
                    </span>
                    <Dropdown
                      ariaLabel={`Map ${field.label}`}
                      value={s.mapping[field.key] || ""}
                      options={[
                        { value: "", label: "Not mapped" },
                        ...s.csvHeaders.map((header) => ({ value: header, label: header })),
                      ]}
                      onChange={(value) => patch({
                        mapping: { ...s.mapping, [field.key]: value },
                        normalized: [], rejectedContacts: [], duplicateContacts: [],
                        rowErrors: [],
                      })}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          {s.csvHeaders.length > 0 && s.normalized.length === 0 && (
            <div className="overflow-hidden rounded-xl border border-line bg-elevated mt-6">
              <div className="flex items-center justify-between border-b border-line px-6 py-4">
                <span className="text-base font-bold text-ink">Parsed Data Preview</span>
                <span className="rounded-md border border-line bg-elevated px-3 py-1 text-sm font-semibold text-ink-2 shadow-sm">
                  {s.rawRows.length - s.excludedRowIndices.length} selected
                </span>
              </div>
              <div className={cn("overflow-x-auto", showAllPreview && "max-h-[420px] overflow-y-auto")}>
                <table className="w-full text-left text-sm text-ink">
                  <thead className="bg-soft text-ink-2 border-b border-line sticky top-0 z-10">
                    <tr>
                      <th className="px-6 py-3 font-semibold w-12">
                        <button onClick={() => patch({ excludedRowIndices: s.excludedRowIndices.length === 0 ? s.rawRows.map((_, i) => i) : [] })}
                          className={cn("h-4 w-4 rounded flex items-center justify-center transition-colors", s.excludedRowIndices.length === 0 ? "bg-brand" : "border border-line bg-elevated")}>
                          {s.excludedRowIndices.length === 0 && <Check className="h-3 w-3 text-white" />}
                        </button>
                      </th>
                      {s.csvHeaders.map((k) => <th key={k} className="px-4 py-3 font-semibold whitespace-nowrap">{k}</th>)}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-line bg-elevated">
                    {(showAllPreview ? s.rawRows : s.rawRows.slice(0, 10)).map((row, i) => {
                      const selected = !s.excludedRowIndices.includes(i);
                      return (
                        <tr key={i} className="hover:bg-soft/50 transition-colors">
                          <td className="px-6 py-3">
                            <button onClick={() => patch({ excludedRowIndices: selected ? [...s.excludedRowIndices, i] : s.excludedRowIndices.filter(x => x !== i) })}
                              className={cn("h-4 w-4 rounded flex items-center justify-center transition-colors", selected ? "bg-brand" : "border border-line bg-elevated")}>
                              {selected && <Check className="h-3 w-3 text-white" />}
                            </button>
                          </td>
                          {s.csvHeaders.map((k, j) => <td key={j} className="max-w-[150px] truncate px-4 py-3 text-ink-2">{row[k]}</td>)}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {s.rawRows.length > 10 && (
                <div className="border-t border-line bg-soft p-3 text-center">
                  <button
                    onClick={() => setShowAllPreview((v) => !v)}
                    className="text-xs font-semibold text-brand hover:underline"
                  >
                    {showAllPreview
                      ? "Show less"
                      : `View all ${s.rawRows.length} rows (... and ${s.rawRows.length - 10} more)`}
                  </button>
                </div>
              )}
            </div>
          )}
        </>

      ) : (
        <div className="space-y-4">
          {s.manualRows.map((row, i) => (
            <div key={i} className="relative rounded-xl border border-line bg-soft/10 p-4 pt-5 mt-2">
              <div className="absolute top-2 left-3 text-[10px] font-bold text-ink-3 flex items-center gap-3">
                <span>ROW {i + 1}</span>
                {Object.values(row).some(v => v === "dummy" || v === "9999999999") && (
                  <button 
                    type="button"
                    onClick={() => {
                      const newRow = { ...row };
                      for (const k in newRow) {
                        if (newRow[k] === "dummy" || newRow[k] === "9999999999") newRow[k] = "";
                      }
                      patch({ manualRows: s.manualRows.map((r, j) => j === i ? newRow : r), normalized: [], rejectedContacts: [], duplicateContacts: [] });
                    }}
                    className="text-brand hover:underline font-normal text-[10px] lowercase"
                  >
                    Clear dummy data
                  </button>
                )}
              </div>
              {s.manualRows.length > 1 && (
                <button aria-label="Remove row" onClick={() => patch({ manualRows: s.manualRows.filter((_, j) => j !== i), normalized: [], rejectedContacts: [], duplicateContacts: [] })}
                  className="absolute top-1.5 right-1.5 rounded-lg p-1.5 text-ink-3 hover:bg-danger-soft hover:text-danger">
                  <X className="h-4 w-4" />
                </button>
              )}
              <div className="mt-4 space-y-4">
                {/* Caller Details */}
                {contactFields.filter((f) => f.key !== "phone_number").length > 0 && (
                  <div className="rounded-lg border border-line bg-soft/40 p-4 shadow-sm">
                    <div className="mb-4 text-[10px] font-bold uppercase tracking-wider text-ink-3">
                      Caller Details
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {contactFields.filter((f) => f.key !== "phone_number").map((f) => (
                        <label key={f.key} className="block relative group">
                          <span className="mb-1 block truncate text-[10px] font-semibold uppercase tracking-wider text-ink-3">
                            {f.label || f.key} {f.required && <span className="text-danger">*</span>}
                          </span>
                          <div className="relative">
                            <input value={row[f.key] ?? ""} placeholder={f.key} autoComplete="off"
                              onChange={(e) => patch({ manualRows: s.manualRows.map((r, j) => (j === i ? { ...r, [f.key]: e.target.value } : r)), normalized: [], rejectedContacts: [], duplicateContacts: [] })}
                              className="w-full rounded-lg border border-line bg-canvas px-3 py-2 font-mono text-sm text-ink focus:border-brand focus:outline-none pr-8" />
                            {(row[f.key] === "dummy" || row[f.key] === "9999999999") && (
                              <button
                                type="button"
                                title="Clear pre-filled dummy value"
                                onClick={(e) => {
                                  e.preventDefault();
                                  patch({ manualRows: s.manualRows.map((r, j) => (j === i ? { ...r, [f.key]: "" } : r)), normalized: [], rejectedContacts: [], duplicateContacts: [] });
                                }}
                                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-ink-3 hover:bg-soft hover:text-ink rounded flex items-center justify-center opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity"
                              >
                                <X className="h-3.5 w-3.5" />
                              </button>
                            )}
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                {/* Dial */}
                <div className="rounded-lg border border-line bg-canvas p-4 shadow-sm flex flex-col sm:flex-row sm:items-center gap-4">
                  <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-wider text-brand shrink-0">
                    Dial
                  </div>
                  <div className="flex-1 w-full max-w-sm">
                    {contactFields.filter((f) => f.key === "phone_number").map((f) => (
                      <label key={f.key} className="block relative group">
                        <div className="flex gap-2">
                          {/* Custom country code dropdown */}
                          <CountryCodePicker
                            value={row[`${f.key}_cc`] ?? "+91"}
                            onChange={(cc) => patch({ manualRows: s.manualRows.map((r, j) => (j === i ? { ...r, [`${f.key}_cc`]: cc } : r)), normalized: [], rejectedContacts: [], duplicateContacts: [] })}
                          />
                          <div className="relative flex-1">
                            <input
                              value={row[f.key] ?? ""}
                              placeholder="98xxxxxxxx"
                              autoComplete="off"
                              onChange={(e) => patch({ manualRows: s.manualRows.map((r, j) => (j === i ? { ...r, [f.key]: e.target.value } : r)), normalized: [], rejectedContacts: [], duplicateContacts: [] })}
                              className="w-full rounded-lg border border-line bg-canvas px-3 py-2 font-mono text-sm text-ink focus:border-brand focus:outline-none pr-8"
                            />
                            {(row[f.key] === "dummy" || row[f.key] === "9999999999") && (
                              <button
                                type="button"
                                title="Clear pre-filled dummy value"
                                onClick={(e) => {
                                  e.preventDefault();
                                  patch({ manualRows: s.manualRows.map((r, j) => (j === i ? { ...r, [f.key]: "" } : r)), normalized: [], rejectedContacts: [], duplicateContacts: [] });
                                }}
                                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-ink-3 hover:bg-soft hover:text-ink rounded flex items-center justify-center opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity"
                              >
                                <X className="h-3.5 w-3.5" />
                              </button>
                            )}
                          </div>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ))}
          <button onClick={() => patch({ manualRows: [...s.manualRows, {}] })} className="flex items-center gap-1.5 text-sm font-semibold text-brand hover:underline mt-2"><Plus className="h-4 w-4" /> Add row</button>
        </div>
        )
      )}

      {(!hideValidate && !s.isRetryLocked) && (
        <>
          <button onClick={() => validateContacts.mutate()}
            disabled={
              !(s.agentId || s.multiAgentConfigId)
              || (s.contactSource === "csv"
                ? s.rawRows.length === 0 || s.rawRows.length === s.excludedRowIndices.length
                : !s.manualRows.some((row) => Object.values(row).some(Boolean)))
              || validateContacts.isPending
            }
            className="w-full rounded-lg bg-ink py-2.5 text-sm font-bold text-canvas hover:opacity-90 disabled:cursor-not-allowed disabled:bg-line disabled:text-ink-3">
            {validateContacts.isPending ? "Checking phone numbers..." : s.normalized.length > 0 ? "Re-validate" : "Validate contacts"}
          </button>
          <p className="text-center font-mono text-[10px] text-ink-3">
            Checks phones and duplicates only. Spoken-field normalisation runs after Launch Batch.
          </p>
        </>
      )}

      {s.rejectedContacts.length > 0 && (
        <div className="overflow-hidden rounded-xl border border-danger/30 bg-danger-soft/10 mt-4">
          <div className="flex items-center justify-between border-b border-danger/20 bg-danger-soft/20 px-4 py-3">
            <span className="text-sm font-semibold text-danger">
              Rejected ({s.rejectedContacts.length})
            </span>
            <span className="text-[10px] text-ink-3">These contacts will not be included in the batch</span>
          </div>
          <div className="relative max-h-[300px] overflow-auto">
            <table className="w-full text-left font-mono text-[10px] text-ink-2">
              <thead className="sticky top-0 border-b border-danger/20 bg-danger-soft/10 z-10">
                <tr>
                  <th className="px-4 py-2 font-semibold text-ink">Row</th>
                  <th className="px-4 py-2 font-semibold text-ink">Name</th>
                  <th className="px-4 py-2 font-semibold text-ink">Phone</th>
                  <th className="px-4 py-2 font-semibold text-ink">Reason</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-danger/10">
                {s.rejectedContacts.map((row, i) => {
                  const reason = row.rejection_reason || s.rowErrors[i]?.reason || "Invalid";
                  return (
                    <tr key={i} className="hover:bg-danger-soft/10 transition-colors">
                      <td className="px-4 py-2 text-ink-3 font-mono">{row.source_row || i + 1}</td>
                      <td className="max-w-[150px] truncate px-4 py-2">{row.name || "—"}</td>
                      <td className="max-w-[150px] truncate px-4 py-2 font-mono">{row.phone_number || row.phone || "—"}</td>
                      <td className="max-w-[220px] truncate px-4 py-2 font-medium text-danger">{reason}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {(s.duplicateContacts || []).length > 0 && (
        <div className="overflow-hidden rounded-xl border border-amber-500/40 bg-amber-50/40 mt-4">
          <div className="flex items-center justify-between border-b border-amber-500/20 bg-amber-100/40 px-4 py-3">
            <span className="text-sm font-semibold text-amber-700">
              Duplicate Contact IDs ({(s.duplicateContacts || []).length})
            </span>
            <span className="text-[10px] text-ink-3">Warning only — these rows will still launch</span>
          </div>
          <div className="relative max-h-[300px] overflow-auto">
            <table className="w-full text-left font-mono text-[10px] text-ink-2">
              <thead className="sticky top-0 border-b border-amber-500/20 bg-amber-50 z-10">
                <tr>
                  <th className="px-4 py-2 font-semibold text-ink">Row</th>
                  <th className="px-4 py-2 font-semibold text-ink">Name</th>
                  <th className="px-4 py-2 font-semibold text-ink">Phone</th>
                  <th className="px-4 py-2 font-semibold text-ink">Contact ID</th>
                  <th className="px-4 py-2 font-semibold text-ink">Reason</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-amber-500/10">
                {(s.duplicateContacts || []).map((row, i) => (
                  <tr key={i} className="hover:bg-amber-100/40 transition-colors">
                    <td className="px-4 py-2 text-ink-3 font-mono">{row.source_row || i + 1}</td>
                    <td className="max-w-[150px] truncate px-4 py-2">{row.name || "—"}</td>
                    <td className="max-w-[150px] truncate px-4 py-2 font-mono">{row.phone_number || row.phone || "—"}</td>
                    <td className="max-w-[150px] truncate px-4 py-2 font-mono font-semibold text-amber-800">{row.contact_id || "—"}</td>
                    <td className="max-w-[220px] truncate px-4 py-2 font-medium text-amber-700" title={row._dup_reason}>{row._dup_reason || "Duplicate contact ID"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {s.normalized.length > 0 && (!hideValidate || s.isRetryLocked) && (
        <div className={cn(
          "overflow-hidden rounded-xl border",
          s.isRetryLocked ? "border-line bg-elevated" : "border-success/40 bg-success-soft/10 mt-6"
        )}>
          <div className={cn(
            "flex items-center justify-between border-b px-4 py-3",
            s.isRetryLocked ? "border-line bg-soft/30" : "border-success/20 bg-success-soft/30 py-2"
          )}>
            <span className={cn(
              "font-semibold",
              s.isRetryLocked ? "text-base text-ink" : "text-sm text-success-dark"
            )}>
              {s.isRetryLocked 
                ? `Contacts to Retry (${s.normalized.length})` 
                : `Batch Preview (${s.normalized.length} ready${s.rejectedContacts.length ? `, ${s.rejectedContacts.length} rejected (saved)` : ""})`}
            </span>
          </div>
          <div className={cn("overflow-x-auto", (s.isRetryLocked || showAllValidated) && "max-h-[400px] overflow-y-auto")}>
            <table className={cn("w-full text-left font-mono", s.isRetryLocked ? "text-xs text-ink" : "text-[10px] text-ink-2")}>
              <thead className={cn(
                "sticky top-0 border-b z-10",
                s.isRetryLocked ? "border-line bg-soft text-ink-2" : "border-success/20 bg-success-soft/20 text-ink"
              )}>
                <tr>
                  {Object.keys(s.normalized[0]).map((k) => <th key={k} className="px-4 py-3 font-semibold">{k}</th>)}
                </tr>
              </thead>
              <tbody className={cn("divide-y", s.isRetryLocked ? "divide-line" : "divide-success/10")}>
                {(s.isRetryLocked || showAllValidated ? s.normalized : s.normalized.slice(0, 4)).map((row, i) => (
                  <tr key={i} className={s.isRetryLocked ? "hover:bg-soft/50 transition-colors" : ""}>
                    {Object.values(row).map((v, j) => <td key={j} className="max-w-[200px] truncate px-4 py-2">{v}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {!s.isRetryLocked && s.normalized.length > 4 && (
            <div className="border-t border-success/20 bg-success-soft/20 p-1.5 text-center">
              <button
                onClick={() => setShowAllValidated((v) => !v)}
                className="font-mono text-[10px] font-semibold text-success-dark hover:underline"
              >
                {showAllValidated
                  ? "Show less"
                  : `View all ${s.normalized.length} rows (... and ${s.normalized.length - 4} more)`}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ── STEP 4 · Scheduling ── */
export function StepSchedule({ s, patch, onLaunch, launching, allValid }: P & { onLaunch: () => void; launching: boolean; allValid: boolean }) {
  const past = s.mode === "later" && !!s.at && new Date(s.at) <= new Date();
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 gap-4">
        {([["now", "Run Now", "Start dialing as soon as created"], ["later", "Target Time", "Pick date & time"]] as const).map(([id, t, sub]) => (
          <button key={id} onClick={() => patch({ mode: id })}
            className={cn("rounded-xl border p-4 text-left", s.mode === id ? "border-brand bg-brand-soft/30" : "border-line hover:bg-soft")}>
            <p className="font-bold text-ink">{t}</p><p className="text-xs text-ink-3">{sub}</p>
          </button>
        ))}
      </div>
      {s.mode === "later" && (
        <div className="grid grid-cols-2 gap-4">
          <label className="block">
            <span className="mb-1 block font-mono text-[10px] font-semibold uppercase tracking-wider text-ink-3">Dial at</span>
            <DateTimePicker
              value={s.at}
              onChange={(val) => patch({ at: val })}
              placeholder="Pick target date & time"
              minDate={new Date().toISOString().slice(0, 10)}
              className={cn("w-full", past && "[&>button]:border-danger/50")}
            />
            {past && <span className="mt-1 block text-xs text-danger">Must be in the future.</span>}
          </label>
          <div>
            <span className="mb-1 block font-mono text-[10px] font-semibold uppercase tracking-wider text-ink-3">Timezone</span>
            <TimezoneSelect ariaLabel="Timezone" value={s.timezone} onChange={(v) => patch({ timezone: v })} />
          </div>
        </div>
      )}


      <button onClick={onLaunch} disabled={!allValid || launching}
        className={cn("flex w-full items-center justify-center gap-2 rounded-xl py-3 text-sm font-bold text-white transition-all", allValid && !launching ? "bg-brand hover:bg-brand-hover shadow-[0_0_15px_rgba(249,115,22,0.25)]" : "cursor-not-allowed bg-line text-ink-3")}>
        {launching ? "Launching…" : "Launch Batch"}
      </button>
    </div>
  );
}
